import { gql } from 'graphql-request';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import useGraphQLClient from '../../../app/hooks/useGraphQLClient';
import { useAuthContext } from '../../auth/contexts/AuthContext';
import IDocumentFile from '../interfaces/IDocumentFile';
import useAppLog from '../../../app/features/log/hooks/useAppLog';

const useDocumentFiles = () => {
	const authContext = useAuthContext();
	const graphQLClient = useGraphQLClient();

	const appLog = useAppLog();

	const { data, error, isFetching } = useQuery<IDocumentFile[] | null, Error>(
		['documentFiles'],
		async () => {
			try {
				const data = (await graphQLClient.request(
					gql`
						query {
							investmentDocuments {
								id
								name
								documentType
								date
								formatTag
								isNew
								middleOfficeDocId
								downloadPath
								fund {
									id
									name
									typeTag
									isRealized
								}
								company {
									id
									name
									group {
										id
										name
									}
								}
							}
						}
					`,
				)) as { investmentDocuments: IDocumentFile[] };
				return data.investmentDocuments;
			} catch (e) {
				appLog.exception(e, { component: 'useDocuments' });
				return null;
			}
		},
		{
			// The query will not execute until the userId exists
			enabled:
				appLog &&
				authContext.currentCompany !== null &&
				authContext.isInvestor === true &&
				authContext.isAuthenticated,
		},
	);

	return useMemo(
		() => ({
			error,
			isFetching,
			documents: data,
		}),
		[error, isFetching, data],
	);
};

export default useDocumentFiles;
